/**
 * InvestorPlansComparisonTable
 *
 * Full feature-grouped Lite / Pro / Plus comparison. Used on both the
 * `/pricing` page and the `/for-investors` page so the matrix stays
 * authoritative in one place.
 *
 * Each row carries an optional `why` string. When present, the row
 * becomes an expandable accordion item: clicking the feature label
 * (or the chevron) reveals a single-sentence "why this matters"
 * explanation in plain English. The table doubles as an education
 * surface — useful for investors evaluating which tier fits.
 *
 * Telemetry (consent-gated):
 *   - investor_table_row_expanded { feature, group, surface }
 */
import { Fragment, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * Each row: { label, lite, pro, plus, why? }
 *  - lite/pro/plus: boolean (or string for "—")
 *  - why: optional plain-English explanation of why the feature matters.
 *    Keep to one sentence. Avoid hype.
 */
export const INVESTOR_PLANS_FEATURE_MATRIX = [
    {
        group: "Report Analysis",
        rows: [
            {
                label: "Standard inspection analysis",
                lite: true,
                pro: true,
                plus: true,
                why: "This is the core report — every finding is organized, severity-tagged, and explained in plain English.",
            },
            {
                label: "ZIP-localized repair exposure",
                lite: true,
                pro: true,
                plus: true,
                why: "Generic internet cost ranges don't help — we tune ranges to your actual ZIP's labor and material market.",
            },
            {
                label: "Basic issue prioritization",
                lite: true,
                pro: true,
                plus: true,
                why: "A long inspection has signal and noise; prioritization separates them so you know where to focus first.",
            },
            {
                label: "PDF export + share links",
                lite: true,
                pro: true,
                plus: true,
                why: "Forward findings to your agent, contractor, or partner without re-typing anything.",
            },
            {
                label: "Premium Closing Strategy on every report",
                lite: false,
                pro: true,
                plus: true,
                why: "Goes beyond findings to give you a sequenced negotiation playbook based on what your inspector flagged.",
            },
        ],
    },
    {
        group: "Negotiation Tools",
        rows: [
            {
                label: "Negotiation leverage scoring",
                lite: false,
                pro: true,
                plus: true,
                why: "Ranks each finding by how much credit it's typically worth at the table, so you concede on cosmetic items and push on structural ones.",
            },
            {
                label: "Seller concession strategy guidance",
                lite: false,
                pro: true,
                plus: true,
                why: "Tells you whether to request a credit, a repair, or a sequenced ultimatum for each finding.",
            },
            {
                label: "Contractor & seller question lists",
                lite: false,
                pro: true,
                plus: true,
                why: "Pre-written questions remove the guesswork when you're under time pressure on the phone with a contractor or seller.",
            },
            {
                label: "Financing & insurance flag highlights",
                lite: false,
                pro: true,
                plus: true,
                why: "Spots findings (active leaks, electrical, roof life) that can derail your loan or insurance underwriting if not addressed pre-close.",
            },
            {
                label: "Saved negotiation summaries",
                lite: false,
                pro: true,
                plus: true,
                why: "Reopen any past negotiation summary instantly — useful when comparing the current property against your last few deals.",
            },
        ],
    },
    {
        group: "Deal Comparison",
        rows: [
            {
                label: "Side-by-side deal comparison dashboard",
                lite: false,
                pro: true,
                plus: true,
                why: "When you're choosing between offers, comparing risk + exposure + leverage in one view beats opening five PDFs.",
            },
            {
                label: "Cross-report search",
                lite: false,
                pro: true,
                plus: true,
                why: "Find every property in your pipeline with a 'roof' issue or 'water intrusion' flag in a single search.",
            },
            {
                label: "Advanced filters",
                lite: false,
                pro: true,
                plus: true,
                why: "Filter your pipeline by risk band, exposure ceiling, or pipeline status to focus on what needs action this week.",
            },
            {
                label: "Advanced multi-property comparison",
                lite: false,
                pro: false,
                plus: true,
                why: "For investors evaluating 5+ properties at once, compare risk, exposure, and leverage across the whole list with one click.",
            },
        ],
    },
    {
        group: "Portfolio Workflow",
        rows: [
            {
                label: "Private investor notes per property",
                lite: false,
                pro: true,
                plus: true,
                why: "Tag each property with negotiation tactics, contractor quotes, or your reasoning — only you see them.",
            },
            {
                label: "Pipeline statuses (Reviewing → Closed)",
                lite: false,
                pro: true,
                plus: true,
                why: "Move properties through Reviewing → Negotiating → Pending → Closed so nothing falls through the cracks.",
            },
            {
                label: "Quote request workflow",
                lite: false,
                pro: true,
                plus: true,
                why: "Send standardized scope-of-work requests to multiple contractors directly from the report findings.",
            },
            {
                label: "Quote tracking dashboard",
                lite: false,
                pro: true,
                plus: true,
                why: "See which contractors have responded, what they quoted, and how their numbers compare to your repair-exposure estimate.",
            },
            {
                label: "Repair-exposure calibration tracker",
                lite: false,
                pro: true,
                plus: true,
                why: "Logs actual contractor quotes against our estimates over time, calibrating future ranges to your specific market.",
            },
            {
                label: "Portfolio / pipeline overview",
                lite: false,
                pro: false,
                plus: true,
                why: "One dashboard rolls up risk distribution, exposure ceiling, and high-priority counts across every property in your pipeline.",
            },
            {
                label: "Actual-vs-estimated quote tracking",
                lite: false,
                pro: false,
                plus: true,
                why: "Build a record of which estimates were too conservative or aggressive so you negotiate harder next time.",
            },
            {
                label: "Bulk upload queue",
                lite: false,
                pro: false,
                plus: true,
                why: "Drop 5–10 inspection PDFs at once; they're analyzed in parallel without you babysitting the queue.",
            },
            {
                label: "Team access (coming soon)",
                lite: false,
                pro: false,
                plus: true,
                why: "Add your partner, analyst, or assistant to the same workspace so you're not the bottleneck.",
            },
        ],
    },
    {
        group: "Export & Sharing",
        rows: [
            {
                label: "CSV export of comparison & reports",
                lite: false,
                pro: true,
                plus: true,
                why: "Pull the data into a spreadsheet or your CRM for tracking, modeling, or sharing with your team.",
            },
            {
                label: "Advanced portfolio + quote exports",
                lite: false,
                pro: false,
                plus: true,
                why: "Export your portfolio's risk profile, exposure ceilings, and quote history in one file — useful for lender or partner reporting.",
            },
        ],
    },
    {
        group: "Support Level",
        rows: [
            {
                label: "Email support",
                lite: true,
                pro: true,
                plus: true,
                why: "We answer every email — this tier is the baseline.",
            },
            {
                label: "Repair-exposure feedback channel",
                lite: false,
                pro: true,
                plus: true,
                why: "A dedicated channel where we review your specific repair-exposure flags and adjust the model if needed.",
            },
            {
                label: "Prioritized support review",
                lite: false,
                pro: true,
                plus: true,
                why: "Pro tickets jump the queue ahead of Lite when we're triaging.",
            },
            {
                label: "Highest support priority",
                lite: false,
                pro: false,
                plus: true,
                why: "Plus tickets get reviewed first, every time.",
            },
        ],
    },
];

function Cell({ value }) {
    return value ? (
        <Check className="w-4 h-4 text-bl-primary inline" aria-hidden />
    ) : (
        <span className="text-bl-muted" aria-hidden>
            —
        </span>
    );
}

function FeatureRow({ row, group, expanded, onToggle, rowId }) {
    const { label, lite, pro, plus, why } = row;
    const hasExplanation = Boolean(why);
    const labelCell = hasExplanation ? (
        <button
            type="button"
            onClick={() => onToggle(rowId)}
            className="w-full flex items-center justify-between gap-2 text-left text-bl group/expander"
            aria-expanded={expanded}
            aria-controls={`row-why-${rowId}`}
            data-testid={`investor-table-row-expander-${rowId}`}
        >
            <span className="text-bl group-hover/expander:text-bl-primary transition-colors">
                {label}
            </span>
            <ChevronDown
                className={
                    "w-3.5 h-3.5 shrink-0 text-bl-muted group-hover/expander:text-bl-primary transition-transform " +
                    (expanded ? "rotate-180" : "")
                }
                aria-hidden
            />
        </button>
    ) : (
        <span className="text-bl">{label}</span>
    );
    return (
        <Fragment>
            <tr
                className={
                    "border-b border-bl last:border-0 " +
                    (expanded ? "bg-bl-teal-tint/10" : "")
                }
                data-testid={`investor-table-row-${rowId}`}
            >
                <td className="px-5 py-3">{labelCell}</td>
                <td className="px-5 py-3 text-center">
                    <Cell value={lite} />
                </td>
                <td className="px-5 py-3 text-center bg-bl-teal-tint/15">
                    <Cell value={pro} />
                </td>
                <td className="px-5 py-3 text-center">
                    <Cell value={plus} />
                </td>
            </tr>
            {expanded && hasExplanation && (
                <tr
                    className="border-b border-bl bg-bl-bg/30"
                    id={`row-why-${rowId}`}
                    data-testid={`investor-table-why-${rowId}`}
                >
                    <td colSpan={4} className="px-5 pt-1 pb-4">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                            Why this matters
                        </div>
                        <p className="mt-1 text-[13px] sm:text-sm text-bl leading-relaxed max-w-3xl">
                            {why}
                        </p>
                        <div className="mt-1 text-[10px] text-bl-muted/80">
                            {group}
                        </div>
                    </td>
                </tr>
            )}
        </Fragment>
    );
}

export default function InvestorPlansComparisonTable({
    testid = "investor-comparison-table",
    matrix = INVESTOR_PLANS_FEATURE_MATRIX,
    surface = "for_investors",
}) {
    const [expandedIds, setExpandedIds] = useState(() => new Set());

    const handleToggle = (rowId) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
                // Fire only on expand, not on collapse. Lets you measure
                // which rows visitors are most curious about.
                const [group, label] = rowId.split("::");
                track("investor_table_row_expanded", {
                    feature: label,
                    group,
                    surface,
                });
            }
            return next;
        });
    };

    return (
        <div className="bl-card overflow-x-auto" data-testid={testid}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left">
                        <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                            Feature
                            <div className="mt-1 normal-case tracking-normal text-[10px] text-bl-muted/80">
                                Tap any row for a plain-English why.
                            </div>
                        </th>
                        <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center">
                            Investor Lite
                            <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl">
                                $29 / mo
                            </div>
                        </th>
                        <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center bg-bl-teal-tint/30">
                            <span className="text-bl-primary">Investor Pro</span>
                            <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl">
                                $79 / mo
                            </div>
                            <div className="mt-0.5 text-[9px] tracking-[0.14em] uppercase text-bl-primary">
                                Best for active investors
                            </div>
                        </th>
                        <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center">
                            Investor Plus
                            <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl">
                                $149 / mo
                            </div>
                            <div className="mt-0.5 text-[9px] tracking-[0.14em] uppercase text-bl-muted">
                                High-volume workflows
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {matrix.map((g) => (
                        <Fragment key={g.group}>
                            <tr className="bg-bl-bg/40">
                                <td
                                    colSpan={4}
                                    className="px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-bl-primary font-medium border-b border-bl"
                                >
                                    {g.group}
                                </td>
                            </tr>
                            {g.rows.map((row) => {
                                const rowId = `${g.group}::${row.label}`;
                                return (
                                    <FeatureRow
                                        key={rowId}
                                        row={row}
                                        group={g.group}
                                        rowId={rowId}
                                        expanded={expandedIds.has(rowId)}
                                        onToggle={handleToggle}
                                    />
                                );
                            })}
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
